import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { TemplateResposta } from '../../types';
import { Plus, Search, Edit, Trash2, FileText } from 'lucide-react';
import { TemplatesService } from '../../services';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/ConfirmationModal';
import Modal from '../../components/ui/Modal';
import QuillEditor from '../../components/Tickets/QuillEditor';
import ReactQuill from 'react-quill';

interface Filters {
  status: string;
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

interface ModalData {
  isOpen: boolean;
  mode: 'create' | 'edit';
  template?: TemplateResposta;
}

const Templates: React.FC = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateResposta[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<Filters>({
    status: 'all'
  });
  const quillRef = useRef<ReactQuill>(null);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    mode: 'create'
  });
  const [formData, setFormData] = useState({
    nome: '',
    conteudo: '',
    ativo: '1'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    templateId: 0,
    templateName: ''
  });

  // Verificar se o usuário tem acesso
  useEffect(() => {
    if (usuario && usuario.nivel === 'usuario') {
      navigate('/dashboard');
      toast.error('Acesso negado. Apenas administradores e desenvolvedores podem acessar esta página.');
      return;
    }

    if (usuario) {
      loadTemplates();
    }
  }, [usuario, navigate]);

  const loadTemplates = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await TemplatesService.getTemplates();
      if (Array.isArray(response)) {
        setTemplates(response);
      } else {
        toast.error('Erro ao carregar templates');
        setTemplates([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para acessar esta página');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar templates');
      }
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };


  const openDeleteModal = (templateId: number, templateName: string) => {
    setDeleteModal({
      isOpen: true,
      templateId,
      templateName
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      templateId: 0,
      templateName: ''
    });
  };

  const confirmDelete = async () => {
    try {
      await TemplatesService.deleteTemplate(deleteModal.templateId);
      toast.success('Template excluído com sucesso');
      loadTemplates();
      closeDeleteModal();
    } catch (error: any) {
      console.error('Erro ao excluir template:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para excluir este template');
      } else {
        toast.error('Erro ao excluir template');
      }
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.conteudo?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filters.status === 'all' ||
      (filters.status === 'active' && template.ativo === 1) ||
      (filters.status === 'inactive' && template.ativo === 0);

    return matchesSearch && matchesStatus;
  });

  // Calcular paginação
  const totalItems = filteredTemplates.length;
  const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);

  // Atualizar paginação quando os filtros mudarem
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalItems,
      totalPages
    }));
  }, [totalItems, totalPages]);

  // Obter itens da página atual
  const getCurrentPageItems = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredTemplates.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const openCreateModal = () => {
    setModalData({ isOpen: true, mode: 'create' });
    setFormData({
      nome: '',
      conteudo: '',
      ativo: '1'
    });
    // Limpar o Quill
    setTimeout(() => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.setText('');
      }
    }, 100);
  };

  const openEditModal = (template: TemplateResposta) => {
    setModalData({ isOpen: true, mode: 'edit', template });
    setFormData({
      nome: template.nome || '',
      conteudo: template.conteudo || '',
      ativo: template.ativo?.toString() || '1'
    });
    // Atualizar o Quill com o conteúdo do template
    setTimeout(() => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.root.innerHTML = template.conteudo || '';
      }
    }, 100);
  };

  const closeModal = () => {
    setModalData({ isOpen: false, mode: 'create' });
    setFormData({ nome: '', conteudo: '', ativo: '1' });
    setIsSubmitting(false);
    // Limpar o Quill
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.setText('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({
      ...prev,
      conteudo: content
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Obter conteúdo do Quill se disponível
    let conteudo = formData.conteudo;
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const quillHTML = quill.root.innerHTML;
      if (quillHTML && quillHTML.trim() !== '<p><br></p>') {
        conteudo = quillHTML;
      }
    }

    if (!formData.nome.trim() || !conteudo.trim() || conteudo.trim() === '<p><br></p>') {
      toast.error('Nome e conteúdo são obrigatórios');
      return;
    }

    setIsSubmitting(true);

    try {
      if (modalData.mode === 'create') {
        await TemplatesService.createTemplate({
          nome: formData.nome.trim(),
          conteudo: conteudo.trim(),
          ativo: parseInt(formData.ativo)
        });
        toast.success('Template criado com sucesso!');
      } else {
        if (modalData.template) {
          await TemplatesService.updateTemplate(modalData.template.id, {
            nome: formData.nome.trim(),
            conteudo: conteudo.trim(),
            ativo: parseInt(formData.ativo)
          });
          toast.success('Template atualizado com sucesso!');
        }
      }

      closeModal();
      loadTemplates();
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      toast.error(error.message || 'Erro ao salvar template');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Templates de Resposta
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Gerencie os templates de resposta para tickets
            </p>
            <div className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Total de templates: {templates.length} • Filtrados: {filteredTemplates.length}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={openCreateModal}
              className="btn btn-primary w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {getCurrentPageItems().length > 0 ? (
                getCurrentPageItems().map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-1.5 sm:p-2 rounded-lg bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 mr-2 sm:mr-3">
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {template.nome || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.ativo === 1 ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                      }`}>
                        {template.ativo === 1 ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => openEditModal(template)}
                          className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 p-1"
                          title="Editar template"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(template.id, template.nome || '')}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1"
                          title="Excluir template"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-gray-500 dark:text-gray-400">
                    <FileText className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-base sm:text-lg font-medium">Nenhum template encontrado</p>
                    <p className="text-xs sm:text-sm">Tente ajustar os filtros de busca ou crie um novo template</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
              Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, totalItems)} de {totalItems} resultados
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2">
              {/* Botão Anterior */}
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                ← Anterior
              </button>

              {/* Números das páginas */}
              <div className="flex items-center justify-center space-x-1 flex-wrap">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border min-w-[2.5rem] sm:min-w-[2.75rem] ${page === pagination.currentPage
                          ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === pagination.currentPage - 2 ||
                    page === pagination.currentPage + 2
                  ) {
                    return <span key={page} className="px-1 sm:px-2 text-gray-400 dark:text-gray-500 text-xs">...</span>;
                  }
                  return null;
                })}
              </div>

              {/* Botão Próximo */}
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === totalPages}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === totalPages
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                Próximo →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Criar/Editar Template */}
      {modalData.isOpen && (
        <Modal
          isOpen={modalData.isOpen}
          onClose={closeModal}
          title={modalData.mode === 'create' ? 'Novo Template' : 'Editar Template'}
          maxWidth="lg"
          closeOnBackdropClick={!isSubmitting}
        >
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            {/* Nome */}
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                Nome *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Digite o nome do template"
                required
              />
            </div>

            {/* Conteúdo */}
            <div>
              <label htmlFor="conteudo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                Conteúdo *
              </label>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <QuillEditor
                  ref={quillRef}
                  value={formData.conteudo}
                  onChange={handleContentChange}
                  placeholder="Digite o conteúdo do template..."
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      ['link', 'image'],
                      ['clean']
                    ],
                    clipboard: {
                      matchVisual: false,
                    },
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline', 'strike',
                    'color', 'background',
                    'list', 'bullet',
                    'align',
                    'link', 'image'
                  ]}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label htmlFor="ativo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                Status
              </label>
              <select
                id="ativo"
                name="ativo"
                value={formData.ativo}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="1">Ativo</option>
                <option value="0">Inativo</option>
              </select>
            </div>

            {/* Botões */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 border border-transparent rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </div>
                ) : (
                  modalData.mode === 'create' ? 'Criar' : 'Salvar'
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir o template "${deleteModal.templateName}"? Esta ação não pode ser desfeita.`}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      )}
    </div>
  );
};

export default Templates;

